import React, {useMemo, useState} from 'react';

import CSSModules from 'react-css-modules';
import {useLocation, useParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';

import {useSelector} from '../../../state/hooks';
import {
  useGetImagesFromLibraryQuery,
  useSearchImagesQuery,
} from '../../../state/imageLibrary/imageLibraryApi';
import type {Image} from '../../../state/imageLibrary/imageLibraryApi';
import {getSelectedGroupId} from '../../../state/team/teamSelectors';
import ImageLibraryPreview from './ImageLibraryPreview';
import {MediaLibrary} from './MediaLibrary';
import {useDeleteModal} from './useDeleteModal';

import styles from './styles.module.scss';

interface ImageLibraryProps {
  startUpload: (files: File[]) => void;
  chooseImage: (image: Image, searchRunId?: string) => void;
  deleteImage: (id: number) => void;
  setIsDeleteModalOpen: (arg: boolean) => void;
  isEduMeLibrary: boolean;
}

const ImageLibraryComponent = ({
  startUpload,
  chooseImage,
  deleteImage,
  setIsDeleteModalOpen,
  isEduMeLibrary,
}: ImageLibraryProps) => {
  const location = useLocation();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const [searchQuery, setSearchQuery] = useState('');

  const courseId = useSelector((state) => state.moduleActivity.get('courseId'));
  const lessons = useSelector((state) => state.moduleActivity.get('lessons'));
  const {lessonKey: currentLessonKey} = useParams<{lessonKey?: string}>();

  const lessonId = useMemo(() => {
    const lessonIndex = lessons.findIndex(
      (lesson: any) => lesson.get('key') === currentLessonKey
    );
    return lessons.toJS()[lessonIndex]?.id;
  }, [lessons, currentLessonKey]);

  const isGuideRoute = location.pathname.includes('guides');
  const {guideId: urlGuideId} = useParams<{guideId?: string}>();
  const guideId = useMemo(
    // using a fallback uuid for new guides so that amplitude can still make the correlation between searching
    // and selecting an image within the guide
    () => {
      if (isGuideRoute) {
        if (urlGuideId) {
          return parseInt(urlGuideId, 10);
        }
        return uuid();
      }
      return null;
    },
    [urlGuideId, isGuideRoute]
  );

  const {currentData: searchResults, isFetching: isSearchLoading} =
    useSearchImagesQuery(
      {
        groupId: isEduMeLibrary ? null : selectedGroupId,
        searchQuery,
        trackingData: {
          courseId: isGuideRoute ? null : courseId,
          lessonId,
          guideId,
        },
      },
      {skip: searchQuery === ''}
    );

  const {
    data: {groupImageMediaLibraryAssetIds, edumeImageMediaLibraryAssetIds} = {
      edumeImageMediaLibraryAssetIds: [],
      groupImageMediaLibraryAssetIds: [],
    },
    isLoading: isLibraryLoading,
  } = useGetImagesFromLibraryQuery(selectedGroupId);

  const {showDeleteModal, onDelete, onCancel, onConfirmDelete} = useDeleteModal(
    deleteImage,
    setIsDeleteModalOpen
  );

  const getImagesToRender = () => {
    const idsForTab = isEduMeLibrary
      ? edumeImageMediaLibraryAssetIds
      : groupImageMediaLibraryAssetIds;

    if (searchResults) {
      return searchResults.images
        .filter((image) => idsForTab.includes(image.mediaLibraryAssetId))
        .map((image) => image.mediaLibraryAssetId);
    }

    return idsForTab;
  };

  const imagesToRender = getImagesToRender();

  const renderImages = imagesToRender.map((mediaLibraryAssetId) => (
    <ImageLibraryPreview
      key={mediaLibraryAssetId}
      mediaLibraryAssetId={mediaLibraryAssetId}
      chooseImage={(chosenImage: Image) =>
        chooseImage(chosenImage, searchResults?.searchRunId)
      }
      onDelete={onDelete}
      isEduMeLibrary={isEduMeLibrary}
    />
  ));

  return (
    <MediaLibrary
      type='image'
      renderMedia={renderImages}
      isLibraryLoading={isLibraryLoading || isSearchLoading}
      showDeleteModal={showDeleteModal}
      hasUploader={!isEduMeLibrary}
      startUpload={startUpload}
      onCancel={onCancel}
      onConfirmDelete={onConfirmDelete}
      onSearch={setSearchQuery}
      searchQuery={searchQuery}
    />
  );
};

export const ImageLibrary = CSSModules(ImageLibraryComponent, styles);
